import type { TrackingMultiUserResponse } from '@api'
import { getCurrency } from '@common/utils/currency.ts'
import type { ChartData } from 'chart.js'
import { format, parseISO } from 'date-fns'
import _ from 'lodash/fp'

import { ChartColors } from '@/components/charts/utils'
import { formatMinutesAndHours, formatSecondsAndMinutes } from '@/utils/helpers'
import { formatHour } from '@/utils/time'

import type {
  AllChatterTableType,
  ChatterTrackingEntryWithDateType,
  ChatterTrackingMetrics,
} from './service/chatter-tracking-service.ts'

function extractRevenue(data: TrackingMultiUserResponse) {
  const entries = new Map<string, number[]>()
  const timePoints = []
  const totalTimePoints = Object.keys(data.revenues!).length
  let index = 0

  for (const [date, values] of Object.entries(data.revenues!)) {
    timePoints.push(date)
    for (const value of values) {
      const existing = entries.get(value.name!)

      if (existing) {
        existing[index] = value.revenue!
        continue
      }
      const valuePoints = Array.from<number>({ length: totalTimePoints })

      valuePoints.fill(0)
      valuePoints[index] = value.revenue!
      entries.set(value.name!, valuePoints)
    }
    index++
  }
  const creators = [...entries.entries()].map(([creator, values]) => ({
    creator,
    values,
  }))

  return { timePoints, creators }
}

export const mapTopChattersActivity = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return
  const dataList = trackingData.dataList.filter(
    ({ data }) => data!.minutesActive! > 0,
  )

  const data: ChartData<'doughnut'> = {
    labels: dataList.map((item) => item.name),
    datasets: [
      {
        data: dataList.map(({ data }) => data!.minutesActive!),
        backgroundColor: dataList.map(
          (data) => colorMap.get(data!.name!) || '',
        ),
        spacing: 1,
      },
    ],
  }

  return data
}

export const mapTopChattersMessageAmount = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return
  const dataList = trackingData.dataList.filter(
    ({ data }) => (data!.sendMessages ?? 0) > 0,
  )

  const data: ChartData<'doughnut'> = {
    labels: dataList.map((item) => item.name),
    datasets: [
      {
        data: dataList.map(({ data }) => data!.sendMessages ?? 0),
        backgroundColor: dataList.map((data) => colorMap.get(data.name!) || ''),
        spacing: 1,
      },
    ],
  }

  return data
}

export const mapTopChattersResponseTime = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return
  const dataList = trackingData.dataList.filter(
    ({ data }) => data!.averageResponseTime! > 0,
  )

  const data: ChartData<'doughnut'> = {
    labels: dataList.map((item) => item.name),
    datasets: [
      {
        data: dataList.map(({ data }) => data!.averageResponseTime!),
        backgroundColor: dataList.map((data) => colorMap.get(data.name!) || ''),
        spacing: 1,
      },
    ],
  }

  return data
}

export const mapTopChattersRevenue = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return

  const sortedTrackingData = Object.values(trackingData.total!)
    .filter((value) => value.revenue! > 0)
    .sort((a, b) => b.revenue! + a.revenue!)

  const data: ChartData<'doughnut'> = {
    labels: sortedTrackingData.map((item) => item.name),
    datasets: [
      {
        data: Object.values(sortedTrackingData.map((item) => item.revenue!)),
        backgroundColor: sortedTrackingData.map(
          (item) => colorMap.get(item.name!) || '',
        ),
        spacing: 1,
      },
    ],
  }

  return data
}

export const mapTopChattersRevenueLegend = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
  showCents = false,
) => {
  if (!trackingData?.dataList?.length) return

  const totalList = trackingData.total!.filter((value) => value.revenue! > 0)

  const total = totalList
    .sort((a, b) => a.revenue! + b.revenue!)
    .map((a) => a.revenue!)
    .reduce((a, b) => a + b, 0)

  return {
    data: totalList.map(({ name, revenue }) => ({
      name,
      value: revenue,
      backgroundColor: colorMap.get(name!) || '',
    })),
    total: getCurrency(total, showCents ? 2 : 0),
    isCurrency: true,
  }
}

export const mapTopChattersActivityLegend = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return

  const dataList = trackingData.dataList.filter(
    ({ data }) => data!.minutesActive! > 0,
  )

  const totalMinutes = _.sumBy(
    'minutesActive',
    dataList.map((data) => data.data),
  )

  return {
    data: _.reverse(
      _.sortBy(
        'value',
        dataList.map(({ name, data }) => ({
          name,
          value: data!.minutesActive,
          backgroundColor: colorMap.get(name!) || '',
        })),
      ),
    ),
    total: formatMinutesAndHours(totalMinutes),
    isMinutes: true,
  }
}

export const mapTopChattersMessageAmountLegend = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return

  const dataList = trackingData.dataList.filter(
    ({ data }) => (data!.sendMessages ?? 0) > 0,
  )

  const totalMessages = _.sumBy(
    'sendMessages',
    dataList.map((data) => data.data),
  )

  return {
    data: _.reverse(
      _.sortBy(
        'value',
        dataList.map(({ name, data }) => ({
          name,
          value: data!.sendMessages,
          backgroundColor: colorMap.get(name!) || '',
        })),
      ),
    ),
    total: totalMessages,
  }
}

export const mapTopChattersResponseTimeLegend = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
) => {
  if (!trackingData?.dataList?.length) return

  const dataList = trackingData.dataList.filter(
    ({ data }) => data!.averageResponseTime! > 0,
  )

  const totalResponseTime = _.sumBy(
    'averageResponseTime',
    dataList.map((data) => data.data),
  )

  return {
    data: _.sortBy(
      'value',
      dataList.map(({ name, data }) => ({
        name,
        value: data!.averageResponseTime,
        backgroundColor: colorMap.get(name!) || '',
      })),
    ),
    total: formatSecondsAndMinutes(totalResponseTime),
    isSeconds: true,
  }
}

const getLabels = (metrics: ChatterTrackingMetrics, hourly: boolean) => {
  return Object.keys(metrics)
    .sort((a, b) => a.localeCompare(b))
    .map((date) => format(parseISO(date), hourly ? 'h aaa' : 'MMM dd'))
}

export const mapTopChatterRevenue = (
  trackingData: TrackingMultiUserResponse | undefined,
  colorMap: Map<string, string>,
  isOneDay?: boolean,
) => {
  if (!trackingData) return

  const { timePoints, creators } = extractRevenue(trackingData)
  const sortedCreators = creators.sort(
    (a, b) =>
      a.values.reduce((a, b) => a + b, 0) + b.values.reduce((a, b) => a + b, 0),
  )

  const res: ChartData<'line'> = {
    labels: timePoints.map((date) =>
      format(parseISO(date), isOneDay ? 'h aaa' : 'MMM dd'),
    ),
    datasets: sortedCreators.map(({ creator, values }) => {
      return {
        label: creator,
        data: values,
        borderColor: colorMap.get(creator) || '',
        backgroundColor: colorMap.get(creator) || '',
      }
    }),
  }

  return res
}

const defaultData: AllChatterTableType[] = []

export const mapAllChatterData = (
  trackingData?: TrackingMultiUserResponse,
): AllChatterTableType[] => {
  if (!trackingData?.dataList?.length) {
    return defaultData
  }

  return trackingData.dataList
    .map((data): AllChatterTableType => {
      const tracking = data.data!

      return {
        userId: data.userId,
        name: data.name!,
        profilePicture: data.profilePicture,
        minutesActive: tracking.minutesActive!,
        minutesInactive: tracking.minutesInactive!,
        totalNetRevenue: tracking.totalNetRevenue!,
        averageResponseTime: Number(tracking.averageResponseTime!.toFixed(0)),
        sendMessages: Number(tracking.sendMessages),
        sentPPVs: tracking.sendMediaMessages!,
        boughtPPVs: tracking.boughtMediaMessages!,
      }
    })
    .sort((a, b) => b.totalNetRevenue - a.totalNetRevenue)
}

export const mapStatisticsData = (
  trackingData: ChatterTrackingMetrics | undefined,
) => {
  if (!trackingData || Object.values(trackingData).length === 0) return

  const chatterData = Object.values(trackingData)
  const dates = Object.keys(trackingData)

  const res: ChatterTrackingEntryWithDateType[] = chatterData.map(
    (tracking, i) => ({
      date: dates[i],
      sentPPVs: tracking.sendMediaMessages,
      boughtPPVs: tracking.boughtMediaMessages,
      ...tracking,
    }),
  )

  return res
}

export const mapChatterTotalSales = (
  metrics: ChatterTrackingMetrics | undefined,
  hourly: boolean,
  shouldShowCents = false,
) => {
  if (!metrics) return

  const netTotal = Object.values(metrics).reduce(
    (sum, current) =>
      sum + (current.netSoldRevenue + (current.netTipsRevenue || 0)),
    0,
  )

  const chartData: ChartData<'line'> = {
    labels: getLabels(metrics, hourly),
    datasets: [
      {
        label: 'Net Revenue PPVs',
        data: Object.values(metrics).map((i) => i.netSoldRevenue),
        borderColor: '#1E84E3',
      },
      {
        label: 'Net Revenue Tips',
        data: Object.values(metrics).map((i) => i.netTipsRevenue || 0),
        borderColor: '#0BC9CD',
      },
    ],
  }

  return { chartData, total: getCurrency(netTotal, shouldShowCents ? 2 : 0) }
}

export const mapChatterPpvPurchase = (
  metrics: ChatterTrackingMetrics | undefined,
  hourly: boolean,
) => {
  if (!metrics) return

  const boughtPpvs = Object.values(metrics)
    .map((item) => item.boughtMediaMessages)
    .reduce((prev, curr) => prev + curr, 0)

  const sentPpvs =
    Object.values(metrics)
      .map((item) => item.sendMediaMessages)
      .reduce((prev, curr) => prev + curr, 0) || 1

  const rate = ((boughtPpvs / sentPpvs) * 100).toFixed(0)

  const res: ChartData<'bar'> = {
    labels: getLabels(metrics, hourly),
    datasets: [
      {
        label: 'Sent PPVs',
        data: Object.values(metrics).map((item) => item.sendMediaMessages),
        backgroundColor: '#1E84E3',
        borderRadius: 8,
      },
      {
        label: 'Bought PPVs',
        data: Object.values(metrics).map((item) => item.boughtMediaMessages),
        backgroundColor: '#0BC9CD',
        borderRadius: 8,
      },
    ],
  }

  return { chartData: res, rate }
}

export const mapChatterSentMessages = (
  metrics: ChatterTrackingMetrics | undefined,
  hourly: boolean,
) => {
  if (!metrics) return

  const total = Object.values(metrics)
    .map((item) => Number(item.sendMessages))
    .reduce((sum, current) => sum + current, 0)

  const res: ChartData<'bar'> = {
    labels: getLabels(metrics, hourly),
    datasets: [
      {
        label: 'Sent Messages',
        data: Object.values(metrics).map((item) => Number(item.sendMessages)),
        backgroundColor: '#0BC9CD',
        borderRadius: 8,
      },
    ],
  }

  return { chartData: res, total }
}

export const mapChatterKeystrokes = (
  metrics: ChatterTrackingMetrics | undefined,
  hourly: boolean,
) => {
  if (!metrics) return

  const total = Object.values(metrics)
    .map((item) => Number(item.keystrokes))
    .reduce((sum, current) => sum + current, 0)

  const res: ChartData<'bar'> = {
    labels: getLabels(metrics, hourly),
    datasets: [
      {
        label: 'Keystrokes',
        data: Object.values(metrics).map((item) => Number(item.keystrokes)),
        backgroundColor: '#1E84E3',
        borderRadius: 8,
      },
    ],
  }

  return { chartData: res, total }
}

export const mapChatterOnlineTime = (
  metrics: ChatterTrackingMetrics | undefined,
) => {
  if (!metrics) return

  const minutesActive = Object.values(metrics).reduce(
    (sum, current) => sum + current.minutesActive,
    0,
  )

  const minutesInactive = Object.values(metrics).reduce(
    (sum, current) => sum + current.minutesInactive,
    0,
  )

  const total = Object.values(metrics).reduce(
    (sum, current) => sum + (current.minutesActive + current.minutesInactive),
    0,
  )

  const res: ChartData<'doughnut'> = {
    labels: [
      `Active Time: ${formatHour(minutesActive)}`,
      `Inactive Time: ${formatHour(minutesInactive)}`,
    ],
    datasets: [
      {
        data: [minutesActive, minutesInactive],
        backgroundColor: ['#1E84E3', '#D2E6F9'],
      },
    ],
  }

  return { chartData: res, total: formatHour(total) }
}

export function generateColorMap(data?: TrackingMultiUserResponse) {
  const colorMap = new Map<string, string>()

  if (!data) return colorMap

  const sortedNames = data.dataList?.map((data) => data.name).sort()

  sortedNames?.forEach((name, index) => {
    colorMap.set(name!, ChartColors[index % ChartColors.length])
  })

  return colorMap
}
